import React, { lazy, Suspense, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState, storeActions } from '../../../store'

import type { MenuItem } from '../../../models/types'

import TemplateGlobal from '../../Templates/TemplateGlobal'
import Logo from '../../Common/Logo'
import Version from '../Common/Version'
import AbsoluteLargeClosingButton from '../../Utils/SubView/AbsoluteLargeClosingButton'
import HomeIcon from '../../Icons/RemoteIcons/HomeIcon'
import { BorneParametersIcon } from '../../Icons/ParameterIcons'
import DashboardLine from './DashboardLine'

const LittleWeather = lazy(() => import('../../Weather/LittleWeatherContainer'))

interface DashboardProps {
  toggleDashboard: () => void
}

const Dashboard = ({ toggleDashboard }: DashboardProps): JSX.Element => {
  const dispatch = useDispatch()
  const translation = useSelector((state: RootState) => state.localeReducer.currentTranslations)
  const localizedMenus = useSelector((state: RootState) => state.localeReducer.localizedMenus)
  const menus = useSelector((state: RootState) => state.retorikReducer.configuration.menu)
  const isUsedOnBorne = useSelector((state: RootState) => state.retorikReducer.configuration.isUsedOnBorne)
  const hideParametersButtonInMenu = useSelector((state: RootState) => state.retorikReducer.configuration.hideParametersButtonInMenu)
  const [justify, setJustify] = useState<string>()
  const [opacityStyle, setOpacityStyle] = useState<string | undefined>('')
  const divRef = useRef<HTMLDivElement>(null)

  const processedMenus = useMemo<Array<{ title?: string; tiles: Array<MenuItem> }>>(() => {
    return localizedMenus || menus?.categories
  }, [localizedMenus, menus])

  /**
   * Check if there is an overflow to adjust justify-content value on menu container
   */
  useLayoutEffect(() => {
    setJustify(divRef?.current?.scrollHeight && divRef?.current?.scrollHeight > divRef?.current?.clientHeight ? 'flex-start' : 'center')
  }, [menus])

  const handleHome = (): void => {
    dispatch(storeActions.directline.sendEvent({ name: 'showHome' }))
    toggleDashboard()
  }

  const handleScroll = (): void => {
    if (divRef.current) {
      const maxScroll = divRef.current.scrollHeight - divRef.current.offsetHeight
      const percentage = Math.ceil((divRef.current.scrollTop / maxScroll) * 20)
      setOpacityStyle(`linear-gradient(to top, black ${100 - percentage}%, transparent 100%)`)
    }
  }

  /**
   * Called in borne mode to open retorik kiosk parameters
   */
  const handleClickParameters = (): void => {
    dispatch(storeActions.utils.askForKioskParametersOpening())
    toggleDashboard?.()
  }

  return (
    <TemplateGlobal onClick={toggleDashboard}>
      <div className='rf-h-full rf-w-full rf-pt-40 large-vertical:rf-pt-48 rf-flex rf-justify-center rf-items-center'>
        {/* Logo */}
        <Logo />

        {/* Weather */}
        <Suspense>
          <LittleWeather />
        </Suspense>

        <div
          ref={divRef}
          className='rf-h-full rf-pt-4 rf-pb-24 rf-px-48 tablet:rf-pl-12 tablet:rf-pr-24 borne:rf-pl-32 borne:rf-pr-56 rf-flex rf-flex-col rf-items-start rf-gap-8 rf-overflow-y-scroll rf-scrollbar-hidden'
          style={{
            justifyContent: justify,
            WebkitMaskImage: opacityStyle
          }}
          onScroll={handleScroll}
        >
          {/* Home */}
          <div
            className='rf-h-14 rf-min-h-14 borne:rf-h-20 borne:rf-min-h-20 rf-px-4 rf-py-3 rf-flex rf-flex-row rf-justify-center rf-items-center rf-gap-4 hover:rf-shadow-[inset_0_0_12px_#00000029] hover:rf-cursor-pointer rf-rounded rf-shadow-[3px_3px_16px_#00000029]'
            style={{
              color: '#575F6B',
              background: 'linear-gradient(to left top, #F2F3F6, #E5E6EC)'
            }}
            onClick={handleHome}
          >
            <HomeIcon className='rf-h-8 borne:rf-h-10' color='#818181' />
            <div className='rf-uppercase'>{translation.home.shorttitle}</div>
          </div>

          {/* Other menus */}
          {processedMenus?.map((menu, key) => {
            return <DashboardLine key={key} {...menu} onClick={toggleDashboard} />
          })}
        </div>

        {/* Parameter button for Retorik Kiosk */}
        {isUsedOnBorne && !hideParametersButtonInMenu ? (
          <button
            // @ts-ignore
            part='retorik-framework-parameters'
            className='rf-absolute rf-left-4 rf-bottom-4 rf-h-8 rf-flex rf-flex-row rf-items-center'
            onClick={handleClickParameters}
          >
            <BorneParametersIcon color='#A4A7AF' />
            <p className='rf-uppercase rf-ml-1 rf-text-xsl rf-text-[#A4A7AF]'>{translation.common.parameters}</p>
          </button>
        ) : (
          <Version />
        )}
      </div>

      {/* Closing button */}
      <AbsoluteLargeClosingButton dashboardVisible={true} onClick={toggleDashboard} />
    </TemplateGlobal>
  )
}

export default Dashboard
